
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Package, AlertTriangle, Edit, Trash2, ZoomIn } from "lucide-react";

interface Product {
  id: string;
  barcode: string;
  name: string;
  boxes: number;
  unitsPerBox: number;
  photos: string[];
  hasCredit: boolean;
  posStatus?: 'found' | 'not-found';
  catalogName?: string;
}

interface ProductListGridProps {
  products: Product[];
  onProductClick: (product: Product) => void;
  onEditProduct: (product: Product) => void;
  onDeleteProduct: (product: Product) => void;
  onImageClick: (imageUrl: string, e: React.MouseEvent) => void;
}

export const ProductListGrid = ({
  products,
  onProductClick,
  onEditProduct,
  onDeleteProduct,
  onImageClick
}: ProductListGridProps) => {
  const getFirstPhoto = (p: any): string | null => {
    const candidates: any = p?.photos ?? p?.photo ?? p?.photoUrl ?? p?.image_url ?? p?.imageUrl ?? null;
    let arr: string[] = [];

    if (Array.isArray(candidates)) {
      arr = candidates.filter((s: any) => typeof s === 'string' && s.length > 0);
    } else if (typeof candidates === 'string') {
      const str = candidates.trim();
      if (str.startsWith('[')) {
        try {
          const parsed = JSON.parse(str);
          if (Array.isArray(parsed)) {
            arr = parsed.filter((s: any) => typeof s === 'string' && s.length > 0);
          }
        } catch {
          arr = [str];
        }
      } else if (str.includes(',')) {
        arr = str.split(',').map(s => s.trim()).filter(Boolean);
      } else if (str.length > 0) {
        arr = [str];
      }
    } else if (candidates && typeof candidates === 'object') {
      if (Array.isArray(candidates)) {
        arr = (candidates as any[])
          .map((it: any) => (typeof it === 'string' ? it : it?.url))
          .filter((s: any) => typeof s === 'string' && s.length > 0);
      } else if (typeof (candidates as any).url === 'string') {
        arr = [(candidates as any).url];
      }
    }

    const filtered = arr
      .map((u) => (typeof u === 'string' ? u.trim() : u))
      .filter((u): u is string => !!u && !u.startsWith('blob:'));

    return filtered.length > 0 ? filtered[0] : null;
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {products.map((product) => (
        <Card 
          key={product.id} 
          className="hover:shadow-md transition-shadow border-0 shadow-sm cursor-pointer relative group"
          onClick={() => onProductClick(product)}
        >
          <CardContent className="p-0">
            {/* Status and Action Bar */}
            <div className="absolute top-2 left-2 right-2 flex justify-between items-start z-10">
              <div className="flex gap-1">
                {product.posStatus === 'found' ? (
                  <div className="w-4 h-4 bg-green-500 rounded-full border-2 border-white shadow-sm" />
                ) : (
                  <div className="w-4 h-4 bg-yellow-500 rounded-full border-2 border-white shadow-sm" />
                )}
                {product.hasCredit && (
                  <AlertTriangle className="w-4 h-4 text-orange-500 bg-white rounded-full" />
                )}
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditProduct(product);
                  }}
                  variant="ghost"
                  size="sm"
                  className="p-1 h-6 w-6 bg-white/80 hover:bg-white"
                >
                  <Edit className="w-3 h-3" />
                </Button>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteProduct(product);
                  }}
                  variant="ghost"
                  size="sm"
                  className="p-1 h-6 w-6 bg-white/80 hover:bg-white text-red-600"
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>

            {/* Image Section */}
            <div className="aspect-square relative">
              {(() => {
                const firstPhoto = getFirstPhoto(product);
                return firstPhoto ? (
                  <div className="w-full h-full relative group">
                    <img
                      src={firstPhoto}
                      alt={`${product.name} photo`}
                      className="w-full h-full object-cover rounded-t cursor-pointer hover:opacity-80 transition-opacity"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).onerror = null;
                        (e.currentTarget as HTMLImageElement).src = '/placeholder.svg';
                      }}
                      onClick={(e) => onImageClick(firstPhoto, e)}
                    />
                    <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <ZoomIn className="w-3 h-3 text-white" />
                    </div>
                  </div>
                ) : (
                  <div className="w-full h-full bg-gray-100 rounded-t flex items-center justify-center">
                    <Package className="w-12 h-12 text-gray-400" />
                  </div>
                );
              })()}
            </div>

            {/* Product Info */}
            <div className="p-3 space-y-2">
              <div>
                <h3 className="font-semibold text-sm line-clamp-2 mb-1">{product.name}</h3>
                {product.catalogName && product.catalogName !== product.name && (
                  <p className="text-xs text-green-600">POS: {product.catalogName}</p>
                )}
              </div>
              
              <div className="space-y-1 text-xs text-gray-500">
                <p className="truncate">#{product.barcode}</p>
                <div className="flex justify-between">
                  <span>Boxes:</span>
                  <span className="font-semibold text-blue-600">{product.boxes}</span>
                </div>
                <div className="flex justify-between">
                  <span>Units/Box:</span>
                  <span>{product.unitsPerBox}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
